import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, ViewChild, Injector } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { UpdoxDirectMessage } from '../updox-direct-message';
import { UpdoxMessagesService } from '../updox-messages.service';
import { MessageAttachment, CcdaData, CcdaVital, CcdaMedication } from '../updox-attachment';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';
import { LoaderService } from '../../../../services/loader.service';
import { MedicationSummaryComponent } from '../../../../health/patient-profile/patient-dashboard/summary/medication-summary/medication-summary.component';
import { MedicationSummaryService } from '../../../../health/patient-profile/patient-dashboard/summary/medication-summary/medication-summary.service';
import { Term } from '../../../../enote/enote_models/term';
import { VitalsService } from '../../../../services/vitals.service';
import { VitalComponent } from '../../vital/vital.component';
import { PatientVital } from '../../vital/PatientVital';
import { VitalEntry } from '../../vital/VitalEntry';
import { Vital } from '../../vital/vital';

type SummaryType = 'problems' | 'medications' | 'allergies';
type PreviewTab = SummaryType | 'vitals' | 'others';

// LOINC observation code → this app's vital "short" identifier
const VITAL_LOINC_MAP: { [code: string]: string } = {
  '8310-5':  'temp',   // Body temperature
  '8867-4':  'pulse',  // Heart rate
  '9279-1':  'rr',     // Respiratory rate
  '8480-6':  'sbp',    // Systolic blood pressure
  '8462-4':  'dbp',    // Diastolic blood pressure
  '2708-6':  'spo2',   // Oxygen saturation (arterial)
  '59408-5': 'spo2',   // Oxygen saturation (pulse oximetry)
  '29463-7': 'wt',     // Body weight
  '3141-9':  'wt',     // Body weight measured
  '8302-2':  'ht',     // Body height
  '39156-5': 'bmi',    // Body mass index
  '2339-0':  'rbg',    // Glucose (random)
  '1558-6':  'fbg',    // Glucose (fasting)
  '72514-3': 'nprs',   // Pain severity 0-10
};

// Display names/units matching the patient dashboard's vitals conventions;
// values are converted into these units before display and prefill.
const VITAL_DISPLAY_LABELS: { [short: string]: string } = {
  temp:  'Temperature (F)',
  pulse: 'Pulse (BPM)',
  rr:    'Respiration (BPM)',
  sbp:   'B/P Systolic',
  dbp:   'B/P Diastolic',
  wt:    'Weight (Lb)',
  ht:    'Height (In)',
  bmi:   'BMI (Lb/m2)',
  spo2:  'SpO2 (%)',
  rbg:   'Blood Glucose (mg/dL)',
  fbg:   'Blood Glucose (mg/dL)',
  nprs:  'Pain',
};

@Component({
  selector: 'app-updox-ccda-preview',
  templateUrl: './ccda-preview.component.html',
})
export class UpdoxCcdaPreviewComponent implements OnChanges, OnDestroy {
  @Input() message: UpdoxDirectMessage;
  // Exactly one of these should be set:
  @Input() attachment: MessageAttachment = null;    // processed attachment
  @Input() metadataIndex: number = null;            // still-pending attachment (job not finished)

  @Output() close = new EventEmitter<void>();
  @Output() chartAdded = new EventEmitter<void>();

  @ViewChild('medSummary') medSummary: MedicationSummaryComponent;
  @ViewChild('ccdaVitalModal') ccdaVitalModal: ModalDirective;

  ccda: CcdaData = null;
  isLoading = false;
  isAddingToChart = false;
  activeTab: PreviewTab = 'problems';

  // --- Vitals ingestion (same add-vital modal used by the patient dashboard) ---
  vitalComponent = VitalComponent;
  vitalInjector: Injector = null;
  vitalModalOpen = false;
  vitalModalLoading = false;
  vitalsAdded = false;
  private vitalsSavedSub: Subscription;

  // --- Other Records ---
  expandedOther = 0;

  // --- Patient verification gate ---
  // No clinical item can be added until staff confirms the patient.
  verifiedPatient: any = null;                                 // confirmed patient
  candidatePatient: any = null;                                // suggested patient awaiting the confirm click
  candidateSource: 'linked' | 'matched' | 'manual' = null;
  matchedPatients: any[] = [];                                 // demographic matches from backend

  // Manual patient search
  patientQuery = '';
  patientResults: any[] = [];
  patientSearchLoading = false;
  showPatientDropdown = false;
  private patientSearchTimer: any;
  private patientSearchVersion = 0;

  // --- Reconciliation state ---
  existingNames: { problems: string[]; medications: string[]; allergies: string[] } =
    { problems: [], medications: [], allergies: [] };
  // Full summary terms, kept so "In Summary" rows can open the edit flow
  existingTerms: { problems: Term[]; medications: Term[]; allergies: Term[] } =
    { problems: [], medications: [], allergies: [] };
  summaryLoading = false;
  addedKeys: string[] = [];          // items added during this session
  private pendingAddKey: string = null;

  constructor(
    private messagesService: UpdoxMessagesService,
    private medicationSummaryService: MedicationSummaryService,
    private globalErrorHandler: GlobalErrorHandlerService,
    private loaderService: LoaderService,
    private vitalsService: VitalsService,
    private injector: Injector,
  ) {
    // VitalComponent handles its own save and emits here on success — the only
    // signal available for marking the C-CDA vitals as ingested.
    this.vitalsSavedSub = this.vitalsService.reloadEnrolledPatientVitalsList
      .subscribe(() => { if (this.vitalModalOpen) { this.vitalsAdded = true; } });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasSource = this.attachment?.id != null || this.metadataIndex != null;
    if ((changes.message || changes.attachment || changes.metadataIndex) && this.message?.id && hasSource) {
      this.resetState();
      this.loadCcda();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.patientSearchTimer);
    this.vitalsSavedSub?.unsubscribe();
  }

  private resetState(): void {
    this.ccda = null;
    this.activeTab = 'problems';
    this.vitalInjector = null;
    this.vitalModalOpen = false;
    this.vitalsAdded = false;
    this.expandedOther = 0;
    this.verifiedPatient = null;
    this.candidatePatient = null;
    this.candidateSource = null;
    this.matchedPatients = [];
    this.patientQuery = '';
    this.patientResults = [];
    this.showPatientDropdown = false;
    this.existingNames = { problems: [], medications: [], allergies: [] };
    this.existingTerms = { problems: [], medications: [], allergies: [] };
    this.addedKeys = [];
    this.pendingAddKey = null;
  }

  loadCcda(): void {
    this.isLoading = true;
    const source = this.attachment?.id != null
      ? { attachment_id: this.attachment.id }
      : { metadata_index: this.metadataIndex };

    this.messagesService.parseCcda(this.message.id, source)
      .then(resp => {
        this.isLoading = false;
        this.ccda = resp?.ccda || null;
        this.matchedPatients = resp?.matched_patients || [];
        if (resp?.linked_patient) {
          this.candidatePatient = resp.linked_patient;
          this.candidateSource = 'linked';
        } else if (this.matchedPatients.length > 0) {
          this.candidatePatient = this.matchedPatients[0];
          this.candidateSource = 'matched';
        }
      })
      .catch(() => {
        this.isLoading = false;
        this.globalErrorHandler.error = 'Failed to parse the clinical document.';
      });
  }

  // --- Patient verification ---

  confirmCandidate(): void {
    if (!this.candidatePatient) { return; }
    this.verifiedPatient = this.candidatePatient;
    this.showPatientDropdown = false;
    this.patientResults = [];
    this.loadExistingSummary();
    this.persistPatientLink();
  }

  // Confirming the document's patient also links them to the message itself,
  // so the inbox chip / detail banner / add-to-chart auto-fill all pick it up.
  private persistPatientLink(): void {
    const p = this.verifiedPatient;
    const msg = this.message; // pin: async handler must update the message the PUT targeted
    if (!p?.id || !msg || msg.patient_id === p.id) { return; }
    this.messagesService.linkPatient(msg.id, p.id)
      .then(resp => {
        // message object is shared with detail/list — mutations update their UI.
        msg.patient_id = resp?.patient_id || p.id;
        msg.patient_name = resp?.patient?.name || p.name || '';
      })
      .catch(() => {
        // Non-blocking: reconciliation still works this session; the link can be
        // made from the message banner instead.
      });
  }

  rejectCandidate(): void {
    // Staff says the suggestion is wrong — fall through to manual search.
    this.candidatePatient = null;
    this.candidateSource = null;
    this.patientQuery = '';
  }

  changePatient(): void {
    this.verifiedPatient = null;
    this.candidatePatient = null;
    this.candidateSource = null;
    this.patientQuery = '';
    this.existingNames = { problems: [], medications: [], allergies: [] };
    this.existingTerms = { problems: [], medications: [], allergies: [] };
    // "Added" marks belong to the previous patient's session — clear them.
    this.addedKeys = [];
    this.pendingAddKey = null;
    this.vitalsAdded = false;
    this.vitalInjector = null;
  }

  pickMatchedPatient(p: any): void {
    this.candidatePatient = p;
    this.candidateSource = 'matched';
  }

  get otherMatches(): any[] {
    if (!this.candidatePatient) { return this.matchedPatients; }
    return this.matchedPatients.filter(p => p.id !== this.candidatePatient.id);
  }

  get candidateStrength(): string {
    return this.candidatePatient?.match_strength || '';
  }

  // --- Manual patient search ---

  onPatientQueryChange(value: string): void {
    clearTimeout(this.patientSearchTimer);
    this.patientResults = [];
    this.showPatientDropdown = false;
    if (!value || value.trim().length < 2) {
      this.patientSearchLoading = false;
      return;
    }
    this.patientSearchLoading = true;
    this.patientSearchTimer = setTimeout(() => this.runPatientSearch(value.trim()), 300);
  }

  private runPatientSearch(query: string): void {
    const version = ++this.patientSearchVersion;
    this.messagesService.searchPatients(query, this.message.practice_id)
      .then(results => {
        if (version !== this.patientSearchVersion) { return; }
        this.patientSearchLoading = false;
        this.patientResults = results || [];
        this.showPatientDropdown = this.patientResults.length > 0;
      })
      .catch(() => {
        if (version !== this.patientSearchVersion) { return; }
        this.patientSearchLoading = false;
      });
  }

  selectSearchedPatient(p: any): void {
    // Manual pick still goes through the confirm step so staff sees the
    // document-vs-chart demographic comparison before unlocking the tabs.
    this.candidatePatient = p;
    this.candidateSource = 'manual';
    this.patientResults = [];
    this.showPatientDropdown = false;
    this.patientQuery = '';
  }

  closePatientDropdownDelayed(): void {
    setTimeout(() => { this.showPatientDropdown = false; }, 150);
  }

  // --- Existing summary / duplicate detection ---

  private loadExistingSummary(): void {
    if (!this.verifiedPatient?.id) { return; }
    this.summaryLoading = true;
    this.medicationSummaryService.getPatientSummaryData(this.verifiedPatient.id, 'all', 'all')
      .then(resp => {
        this.summaryLoading = false;
        this.existingNames = {
          problems:    this.extractNames(resp['problems']),
          medications: this.extractNames(resp['medications']),
          allergies:   this.extractNames(resp['allergies']),
        };
        // Same mapping the patient summary listing uses before opening edit
        this.existingTerms = {
          problems:    (resp['problems']    || []).map(t => new Term('', '').load_from_json2(t)),
          medications: (resp['medications'] || []).map(t => new Term('', '').load_from_json2(t)),
          allergies:   (resp['allergies']   || []).map(t => new Term('', '').load_from_json2(t)),
        };
        this.loaderService.hide();
      })
      .catch(() => {
        this.summaryLoading = false;
        this.loaderService.hide();
      });
  }

  private extractNames(arr: any[]): string[] {
    return (arr || [])
      .map(t => String(t.d_name || t.name || '').toLowerCase().trim())
      .filter(n => n.length > 0);
  }

  itemKey(type: SummaryType, name: string): string {
    return type + ':' + (name || '').toLowerCase().trim();
  }

  isAdded(type: SummaryType, name: string): boolean {
    return this.addedKeys.includes(this.itemKey(type, name));
  }

  isInChart(type: SummaryType, name: string): boolean {
    const n = (name || '').toLowerCase().trim();
    if (!n) { return false; }
    return (this.existingNames[type] || []).some(e => e === n || e.includes(n) || n.includes(e));
  }

  // --- Add item to patient summary ---

  addCcdaItem(type: SummaryType, name: string, onsetRaw: string = null, code: string = null): void {
    if (!this.verifiedPatient || !name || !this.medSummary) { return; }
    this.pendingAddKey = this.itemKey(type, name);
    this.medSummary.openWithPrefill(type, name, this.formatCcdaDate(onsetRaw), code);
  }

  // "Metformin 500 MG Oral Tablet" → "Metformin": strength/route/form have their
  // own columns, so the name column and the add/duplicate flows use the bare
  // drug name. Cut at the first standalone strength token, then trim trailing
  // form/route words.
  medDisplayName(m: CcdaMedication): string {
    let name = (m?.name || '').trim();
    name = name.replace(/\s+\d+(\.\d+)?([-/]\d+(\.\d+)?)?\s*(mg|mcg|g|gram|ml|%|unit|units|iu)?\b.*$/i, '');
    name = name.replace(/\s+(oral|delayed release|extended release|chewable)?\s*(tablet|capsule|tab|cap|solution|suspension|syrup|injection|cream|ointment|gel|patch|inhaler|spray|drops?|lotion|powder|suppository)s?\s*$/i, '');
    return name.trim() || (m?.name || '');
  }

  // Medications carry structured details — hand them to the Add modal's generic
  // control mapper so Form/Route/Frequency dropdowns come pre-selected.
  addCcdaMedication(m: CcdaMedication): void {
    const name = this.medDisplayName(m);
    if (!this.verifiedPatient || !name || !this.medSummary) { return; }
    this.pendingAddKey = this.itemKey('medications', name);
    this.medSummary.openWithPrefill('medications', name, null, null, {
      form:      m.form,
      route:     m.route,
      frequency: m.frequency,
    });
  }

  // "In Summary" rows: open the same Change Status popup the patient summary
  // uses (with its pencil escalating to full edit) instead of adding a duplicate.
  editExistingItem(type: SummaryType, name: string): void {
    if (!this.medSummary || !name) { return; }
    const n = name.toLowerCase().trim();
    const term = (this.existingTerms[type] || []).find(t => {
      const tn = String(t.d_name || t.name || '').toLowerCase().trim();
      return tn === n || tn.includes(n) || n.includes(tn);
    });
    if (term) { this.medSummary.openUpdateTermStatusModel(term, type); }
  }

  onSummarySaved(): void {
    if (this.pendingAddKey && !this.addedKeys.includes(this.pendingAddKey)) {
      this.addedKeys = [...this.addedKeys, this.pendingAddKey];
    }
    this.pendingAddKey = null;
    this.loadExistingSummary();
  }

  // --- Vitals ingestion ---

  vitalShortFor(v: CcdaVital): string {
    return VITAL_LOINC_MAP[v?.code] || null;
  }

  // Row presentation: recognized vitals use the dashboard's naming/units with
  // the value converted; unrecognized ones show the document's wording (minus
  // the "Body " prefix) with the raw value + unit.
  vitalDisplay(v: CcdaVital): { name: string; value: string } {
    const short = this.vitalShortFor(v);
    if (short) {
      return {
        name:  VITAL_DISPLAY_LABELS[short],
        value: this.convertVitalValue(short, v) || v.value,
      };
    }
    return {
      name:  (v.name || '').replace(/^body\s+/i, ''),
      value: [v.value, v.unit].filter(x => x).join(' '),
    };
  }

  get mappedVitalsCount(): number {
    return (this.ccda?.vitals || []).filter(v => this.vitalShortFor(v)).length;
  }

  // Opens the same VitalComponent modal the patient dashboard uses for
  // "Add Vitals", pre-filled with the document's recognized readings. Saving
  // goes through the normal vitals flow (PatientVital + VitalEntry records,
  // alerts, RPM hooks) — nothing C-CDA-specific is persisted.
  addVitalsToPatient(): void {
    if (!this.verifiedPatient?.id || this.vitalModalLoading) { return; }
    this.vitalModalLoading = true;
    this.vitalsService.getVitals(this.verifiedPatient.id)
      .then(resp => {
        this.vitalModalLoading = false;
        const defs = (resp || []).map(v => new Vital().load_from_json(v));
        this.vitalInjector = Injector.create(
          [
            { provide: 'patient_id',    useValue: this.verifiedPatient.id },
            { provide: 'patient_vital', useValue: this.buildPrefilledVital(defs) },
          ],
          this.injector);
        this.vitalModalOpen = true;
        this.ccdaVitalModal.show();
      })
      .catch(() => {
        this.vitalModalLoading = false;
        this.globalErrorHandler.error = 'Failed to load the vitals form.';
      });
  }

  closeVitalModal(): void {
    this.ccdaVitalModal.hide();
    // Destroy the outlet-hosted VitalComponent so reopening starts clean.
    setTimeout(() => { this.vitalModalOpen = false; }, 500);
  }

  // Mirrors VitalComponent.initializeVitalEntries(), then fills the entries
  // from the C-CDA readings, converted into the units the form expects
  // (temperature °F, weight lb, height in).
  private buildPrefilledVital(defs: Vital[]): PatientVital {
    const pv = new PatientVital();
    pv.patient_id = this.verifiedPatient.id;
    pv.vital_entries = defs.filter(v => !Vital.independent_vitals.includes(v.short))
      .map(vital => new VitalEntry().initializeVitalEntry(vital));
    pv.initializeIndependentVitalEntry(defs);

    (this.ccda?.vitals || []).forEach(cv => {
      const short = this.vitalShortFor(cv);
      const value = this.convertVitalValue(short, cv);
      if (!short || value == null) { return; }

      if (short === 'wt') {
        // The form binds p_weight (lb); save() derives the kg entry from it.
        pv.p_weight = value;
      } else if (short === 'ht') {
        // The form binds heightInInch; save() derives the entry from it.
        pv.heightInInch = value;
      } else if (short === 'rbg' || short === 'fbg') {
        // The form's glucose input binds glucose_value alongside the entry.
        pv.glucose_value = value;
        const entry = pv.vital_entries.find(e => e.vital?.short === short);
        if (entry) { entry.value = value; }
      } else {
        const entry = pv.vital_entries.find(e => e.vital?.short === short);
        if (!entry) { return; }
        entry.value = value;
        // Same range-matching VitalComponent.changeVital() applies on input:
        // pick the vital definition whose [min, max] brackets the value so the
        // descriptor is computed against the right range row.
        const num = parseFloat(value);
        const ranged = defs.find(d => d.short === short && !isNaN(num) &&
          num >= parseFloat(d.min_val) && num <= parseFloat(d.max_val));
        if (ranged) {
          entry.vital = ranged;
          entry.vital_id = ranged.id;
        }
      }
    });

    const readingDate = this.ccdaVitalReadingDate();
    if (readingDate) { pv.reading_date = readingDate; }
    return pv;
  }

  // C-CDA values arrive in the document's units — convert to the form's.
  private convertVitalValue(short: string, cv: CcdaVital): string {
    const num = parseFloat(cv?.value);
    if (short == null || isNaN(num)) { return null; }
    const unit = (cv.unit || '').toLowerCase();

    if (short === 'temp' && (unit === 'cel' || unit === 'c' || unit === '°c')) {
      return (num * 9 / 5 + 32).toFixed(1);
    }
    if (short === 'wt') {
      if (unit === 'kg') { return (num * 2.2046).toFixed(2); }
      if (unit === 'g')  { return (num / 1000 * 2.2046).toFixed(2); }
      return num.toFixed(2); // lb / [lb_av]
    }
    if (short === 'ht') {
      if (unit === 'cm') { return (num / 2.54).toFixed(2); }
      if (unit === 'm')  { return (num * 39.37).toFixed(2); }
      return num.toFixed(2); // in / [in_i]
    }
    return cv.value;
  }

  // Reading datetime for the form's datetime-local input, taken from the
  // first dated C-CDA vital: YYYYMMDD[HHMMSS±ZZZZ] → YYYY-MM-DDTHH:mm:ss
  private ccdaVitalReadingDate(): string {
    const raw = (this.ccda?.vitals || []).map(v => v.date).find(d => d && d.length >= 8);
    if (!raw) { return null; }
    const date = `${raw.substring(0, 4)}-${raw.substring(4, 6)}-${raw.substring(6, 8)}`;
    const time = raw.length >= 14
      ? `${raw.substring(8, 10)}:${raw.substring(10, 12)}:${raw.substring(12, 14)}`
      : '00:00:00';
    return `${date}T${time}`;
  }

  // --- Other Records ---

  toggleOtherSection(index: number): void {
    this.expandedOther = this.expandedOther === index ? -1 : index;
  }

  // ICD-10 shown in the stored/transmitted dotless form (E11.9 → E119),
  // matching how codes appear elsewhere in the app.
  icd10Display(code: string): string {
    return (code || '').replace(/\./g, '');
  }

  // Staff-facing status: is this item current for the patient or not.
  // C-CDA statuses vary (active/completed/resolved/discontinued/…) — anything
  // active-ish reads "Current", anything else "Past", unknown stays blank.
  statusLabel(status: string): string {
    if (!status) { return ''; }
    const s = status.toLowerCase();
    const current = ['active', 'present', 'current', 'ongoing', 'intended'];
    return current.includes(s) || s.startsWith('continue') ? 'Current' : 'Past';
  }

  // C-CDA effectiveTime values come as YYYYMMDD[HHMMSS] — show MM/DD/YYYY.
  formatCcdaDate(raw: string): string {
    if (!raw || raw.length < 8) { return raw || null; }
    return raw.substring(4, 6) + '/' + raw.substring(6, 8) + '/' + raw.substring(0, 4);
  }

  // trackBy fns keep *ngFor from re-creating DOM rows on every change-detection
  // cycle (otherMatches and the ccda arrays produce fresh references).
  trackByPatientId(_: number, p: any): number { return p.id; }
  trackByIndex(index: number): number { return index; }

  // --- Display helpers ---

  get patientNameInDoc(): string {
    if (!this.ccda?.patient) { return ''; }
    return `${this.ccda.patient.first_name || ''} ${this.ccda.patient.last_name || ''}`.trim();
  }

  get docGenderLabel(): string {
    const g = this.ccda?.patient?.gender;
    if (g === 'M') { return 'Male'; }
    if (g === 'F') { return 'Female'; }
    return g || '';
  }

  patientInitialFor(p: any): string {
    return p?.name?.charAt(0)?.toUpperCase() || '?';
  }

  patientDemographics(p: any): string {
    if (!p?.complete_detail) { return ''; }
    const dashIdx = p.complete_detail.lastIndexOf(' - ');
    const head = dashIdx >= 0 ? p.complete_detail.substring(0, dashIdx) : p.complete_detail;
    const commaIdx = head.indexOf(', ');
    return commaIdx >= 0 ? head.substring(commaIdx + 2) : '';
  }

  patientPhone(p: any): string {
    if (!p?.complete_detail) { return ''; }
    const dashIdx = p.complete_detail.lastIndexOf(' - ');
    return dashIdx >= 0 ? p.complete_detail.substring(dashIdx + 3).trim() : '';
  }

  // --- Add whole C-CDA file to chart (existing feature) ---

  get canAddToChart(): boolean {
    return this.attachment?.id != null && !!(this.verifiedPatient?.id || this.message?.patient_id);
  }

  addCcdaToChart(): void {
    if (this.isAddingToChart || !this.canAddToChart) { return; }
    this.isAddingToChart = true;
    const msg = this.message; // pin: async handler must update the message the POST targeted
    const patientName = this.verifiedPatient?.name || '';
    this.messagesService.addToChart({
      id:            msg.id,
      attachment_id: this.attachment.id,
      patient_id:    this.verifiedPatient?.id || msg.patient_id || undefined,
      name:          `C-CDA: ${msg.subject || 'Clinical Summary'}`,
      date:          new Date().toISOString().substring(0, 10),
    }).then((resp: any) => {
      this.isAddingToChart = false;
      if (resp?.message_patient_id) {
        msg.patient_id   = resp.message_patient_id;
        msg.patient_name = resp.message_patient_name || patientName;
      }
      this.chartAdded.emit();
    }).catch(resp => {
      this.isAddingToChart = false;
      this.globalErrorHandler.error = resp?.error?.error || 'Failed to add C-CDA to chart.';
    });
  }
}
